import { Dialog, Box } from '@mui/material';
import React, { useContext, useState, useRef, useCallback } from 'react';

const initialContext = {
  open: false,
  content: null,
  setContent: () => null,
  setOpen: () => null,
  handleModalClose: () => null,
  updateModalSettings: () => null,
  setRevertableContent: () => null,
  handleModalBackBtn: () => null,
  hasPreviousContentState: false,
  revertableState: {},
  setRevertableState: () => null,
};

export const UseModalContext = React.createContext<{
  open: boolean;
  content: JSX.Element | null;
  setContent: (val: JSX.Element | null) => void;
  setOpen: (open: boolean) => void;
  handleModalClose: () => void;
  updateModalSettings: (obj: ModalSettings) => void;
  setRevertableContent: (newValue: any) => void;
  handleModalBackBtn: () => void;
  hasPreviousContentState: boolean;
  revertableState: { [index: string]: string };
  setRevertableState: (obj: { [index: string]: string }) => void;
}>(initialContext);

export const useModal = (): {
  open: boolean;
  content: JSX.Element | null;
  setContent: (val: JSX.Element | null) => void;
  setOpen: (open: boolean) => void;
  handleModalClose: () => void;
  updateModalSettings: (obj: ModalSettings) => void;
  setRevertableContent: (newValue: any) => void;
  handleModalBackBtn: () => void;
  hasPreviousContentState: boolean;
  revertableState: { [index: string]: string };
  setRevertableState: (obj: { [index: string]: string }) => void;
} => useContext(UseModalContext);

export interface ModalSettings {
  fullWidth?: boolean;
  maxWidth?: false | 'md' | 'xs' | 'sm' | 'lg' | 'xl';
  useBackdropClick?: boolean;
  fullWidthContent?: boolean;
}

const initialModalSettings: ModalSettings = {
  fullWidth: false,
  maxWidth: 'md',
  useBackdropClick: false,
  fullWidthContent: true,
};

export const ModalProvider = ({ children }: { children: React.ReactNode }) => {
  const [open, setOpen] = useState<boolean>(initialContext.open);
  const [content, setContent] = useState<JSX.Element | null>(initialContext.content);
  const [modalSettings, setModalSettings] = useState<ModalSettings>(initialModalSettings);

  // Modal state to save for back button functionality
  const [revertableState, setRevertableState] = useState<{ [index: string]: string }>({});

  const updateModalSettings = (obj: ModalSettings) => {
    const newSettings = { ...modalSettings, ...obj };
    setModalSettings(newSettings);
  };

  const handleModalClose = () => {
    previousContentState.current = null;
    setRevertableState({});
    setContent(null);
    setModalSettings(initialModalSettings);
    setOpen(false);
  };

  const onClose = (event: MouseEvent, reason: string) => {
    if (reason && reason === 'backdropClick' && !modalSettings.useBackdropClick) return;
    handleModalClose();
  };

  const previousContentState = useRef<JSX.Element | null>(initialContext.content);

  // Use this instead of setContent to support a modal back button
  //   needed for some nested modals
  const setRevertableContent = useCallback(
    (newValue: JSX.Element) => {
      previousContentState.current = content; // Save current content as previous
      setContent(newValue); // Update the content
    },
    [content],
  );

  const handleModalBackBtn = useCallback(() => {
    if (previousContentState.current !== null) {
      setContent(previousContentState.current); // Revert to previous modal content
    }
  }, []);

  const hasPreviousContentState = previousContentState.current !== null;

  const pFactor = modalSettings.fullWidthContent ? 0 : 5;

  return (
    <UseModalContext.Provider
      value={{
        open,
        setOpen,
        content,
        setContent,
        handleModalClose,
        updateModalSettings,
        setRevertableContent,
        handleModalBackBtn,
        hasPreviousContentState,
        revertableState,
        setRevertableState,
      }}
    >
      {children}
      {open && (
        <Dialog
          maxWidth={modalSettings.maxWidth}
          fullWidth={modalSettings.fullWidth}
          open={open}
          onClose={onClose}
          aria-labelledby="alert-dialog-title"
          aria-describedby="alert-dialog-description"
          disableScrollLock
        >
          <Box
            maxHeight="calc(100vh - 64px)"
            p={pFactor}
            sx={{
              '> *': {
                maxHeight: (theme) => `calc(100vh - 64px - ${theme.spacing(pFactor * 2)})`,
              },
            }}
          >
            {content}
          </Box>
        </Dialog>
      )}
    </UseModalContext.Provider>
  );
};
